"use client"

import * as React from "react"
import { UserPreferencesService } from '@/lib/api/user-preferences-service'
import { useBrandThemeStore } from '@/store/brand-theme-store'
import { useLayoutPrefsStore } from '@/store/layout-prefs-store'

type Theme = "dark" | "light" | "system"
type ResolvedTheme = "dark" | "light"

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
  attribute?: string
  enableSystem?: boolean
  disableTransitionOnChange?: boolean
}

type ThemeProviderState = {
  theme: Theme
  resolvedTheme: ResolvedTheme
  setTheme: (theme: Theme) => void
}

const getSystemTheme = (): ResolvedTheme => {
  if (typeof window !== 'undefined') {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  }
  return "light"
}

const initialState: ThemeProviderState = {
  theme: "system",
  resolvedTheme: "light",
  setTheme: () => null,
}

const ThemeProviderContext = React.createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "vite-ui-theme",
  attribute = "class",
  enableSystem = true,
  disableTransitionOnChange = false,
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = React.useState<Theme>(
    () => {
      if (typeof window !== 'undefined') {
        return (localStorage?.getItem(storageKey) as Theme) || defaultTheme
      }
      return defaultTheme
    }
  )

  // Compute resolved theme (actual "dark" or "light" value)
  const [resolvedTheme, setResolvedTheme] = React.useState<ResolvedTheme>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage?.getItem(storageKey) as Theme
      const currentTheme = stored || defaultTheme
      if (currentTheme === "system") {
        return getSystemTheme()
      }
      return currentTheme as ResolvedTheme
    }
    return "light"
  })

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const root = window.document.documentElement

      // Remove existing theme classes
      root.classList.remove("light", "dark")

      if (theme === "system" && enableSystem) {
        const systemTheme = getSystemTheme()
        root.classList.add(systemTheme)
        root.setAttribute('data-theme', systemTheme)
        setResolvedTheme(systemTheme)
        return
      }

      root.classList.add(theme)
      root.setAttribute('data-theme', theme)
      setResolvedTheme(theme as ResolvedTheme)
    }
  }, [theme, enableSystem])

  // Listen for system theme changes
  React.useEffect(() => {
    if (typeof window === 'undefined' || theme !== "system") return

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    const handleChange = (e: MediaQueryListEvent) => {
      const newTheme = e.matches ? "dark" : "light"
      setResolvedTheme(newTheme)
      document.documentElement.classList.remove("light", "dark")
      document.documentElement.classList.add(newTheme)
      document.documentElement.setAttribute('data-theme', newTheme)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [theme])

  React.useEffect(() => {
    if (typeof window === 'undefined') return

    if (disableTransitionOnChange) {
      document.documentElement.classList.add('disable-transitions')
      const timeout = setTimeout(() => {
        document.documentElement.classList.remove('disable-transitions')
      }, 0)
      return () => clearTimeout(timeout)
    }
  }, [theme, disableTransitionOnChange])

  // Sync theme from backend on mount (overrides localStorage if backend has a saved value).
  // Also kicks the brand-theme store's backend hydration in the same pass so the user's
  // saved brand color is applied on EVERY page (not only when they open settings).
  React.useEffect(() => {
    let cancelled = false
    UserPreferencesService.getUserContext()
      .then((ctx) => {
        if (!cancelled && ctx?.theme && typeof ctx.theme === 'string') {
          const backendTheme = ctx.theme as Theme
          if (backendTheme !== theme) {
            if (typeof window !== 'undefined') {
              localStorage?.setItem(storageKey, backendTheme)
            }
            setTheme(backendTheme)
          }
        }
      })
      .catch(() => {}) // Silently ignore — user may not be authenticated yet
    // Hydrate brand-theme + layout-prefs stores (each does its own try/catch — safe to fire-and-forget).
    useBrandThemeStore.getState().hydrateFromBackend()
    useLayoutPrefsStore.getState().hydrateFromBackend()
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    theme,
    resolvedTheme,
    setTheme: (newTheme: Theme) => {
      if (typeof window !== 'undefined') {
        localStorage?.setItem(storageKey, newTheme)
      }
      setTheme(newTheme)
      // Persist to backend (fire-and-forget)
      UserPreferencesService.updateUserContext({ theme: newTheme }).catch(() => {})
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = React.useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider")

  // Return resolvedTheme as 'theme' for backward compatibility
  // Components using `theme === 'dark'` will now work correctly even when system theme is set
  return {
    ...context,
    theme: context.resolvedTheme, // Override theme with resolved value for easier usage
    rawTheme: context.theme,      // Original theme setting (system/dark/light)
  }
}
